import { FC, useEffect } from 'react';
import { ApolloQueryResult } from '@apollo/client';
import {
  Badge,
  Container,
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';

import {
  DataRoomObjectFragment,
  GetProblemFilesQuery,
  ProblemFilesFragment,
} from '../../graphql/generated';
import { ProblematicFilesResultsList } from '../problematic-files-results/problematic-files-results';
import { ProblematicFilesTreatyFilter } from '../problematic-files-treaty-filter/problematic-files-treaty-filter';

type Props = {
  treaties: DataRoomObjectFragment[];
  problemTypeObjects: {
    files: ProblemFilesFragment[];
    problemType: string;
    problemDescription: string;
    count: number;
  }[];
  refetch: (variables?: {
    cedants?: string[];
  }) => Promise<ApolloQueryResult<GetProblemFilesQuery>>;
  params: {
    treaties: string[];
    problemType: string;
  };
  setProblemTypeParam: (problemType: string) => void;
  setTreatyParam: (treaties: string[]) => void;
};

export const ProblematicFilesContainer: FC<Props> = ({
  treaties,
  problemTypeObjects,
  refetch,
  params,
  setProblemTypeParam,
  setTreatyParam,
}) => {
  //set treaty params on intial page load
  useEffect(() => {
    if (params.treaties.length > 0 && params.problemType !== '') {
      return;
    }
    setProblemTypeParam('PII Awaiting Approval');
    setTreatyParam(
      treaties.map((cedantDeet) => {
        return cedantDeet.cedant;
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //Find probType from url params - convert to index to be used for default tab on page load
  const tabIndex = problemTypeObjects.findIndex(
    (obj) => obj.problemType === params.problemType
  );

  const handleTabChange = (index: number) => {
    if (problemTypeObjects.length > 0) {
      setProblemTypeParam(problemTypeObjects[index].problemType);
    }
  };

  return (
    <Container display="flex" flex="1" flexDirection="column" maxW="100%" p="1">
      <Flex
        gridGap="4"
        flexWrap="wrap"
        alignItems="center"
        width={{ xs: '40', sm: '96' }}
        mb={5}
        data-testid="treaty-filter"
      >
        <ProblematicFilesTreatyFilter
          params={params}
          setTreatyParam={setTreatyParam}
          refetch={refetch}
          treaties={treaties}
        />
      </Flex>
      <Tabs
        isLazy
        onChange={handleTabChange}
        colorScheme="blue"
        variant="enclosed-colored"
        defaultIndex={tabIndex || 0}
      >
        <TabList>
          {problemTypeObjects.map((object) => {
            return (
              <Tab
                key={object.problemType}
                data-testid={`tab-header-${object.problemType}`}
                p={3}
              >
                {object.problemType}
                <Badge
                  ml="1"
                  variant="solid"
                  colorScheme="blue"
                  borderRadius="full"
                  data-testid={`tab-badge-${object.problemType}`}
                >
                  {object.count}
                </Badge>
              </Tab>
            );
          })}
        </TabList>
        <TabPanels>
          {problemTypeObjects.map((object) => {
            return (
              <TabPanel key={object.problemType}>
                <Flex justifyContent="space-between" flexDirection="row">
                  <Text
                    data-testid={`problem-description-${object.problemType}`}
                  >
                    {object.problemDescription}
                  </Text>
                  <Text
                    color="gray.500"
                    fontWeight="500"
                    minWidth="24rem"
                    data-testid="result-count"
                    textAlign={{ sm: 'left', md: 'right' }}
                  >
                    Showing {object.count} results
                  </Text>
                </Flex>
                <ProblematicFilesResultsList
                  files={object.files}
                  problemType={object.problemType}
                  count={object.count}
                />
              </TabPanel>
            );
          })}
        </TabPanels>
      </Tabs>
    </Container>
  );
};
